import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getNotifications, markAllNotificationsAsRead, markNotificationAsRead } from '@/service/notifications'
import type { Notification, NotificationStats } from '@/types/notification'

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const loading = ref(false)

  const unreadNotifications = computed(() => notifications.value.filter((n) => !n.readAt))

  const stats = computed<NotificationStats>(() => ({
    total: notifications.value.length,
    unread: unreadNotifications.value.length,
  }))

  const allNotifications = computed(() => notifications.value)

  const fetchNotifications = async () => {
    loading.value = true
    try {
      const { onSuccess } = getNotifications(false)

      onSuccess((response) => {
        notifications.value = response.data || []
        loading.value = false
      })
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      loading.value = false
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      const { onSuccess } = markNotificationAsRead(notificationId)

      onSuccess(() => {
        const notification = notifications.value.find((n) => n.id === notificationId)

        if (notification) {
          notification.readAt = new Date().toISOString()
        }
      })
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      const { onSuccess } = markAllNotificationsAsRead()

      onSuccess(() => {
        const now = new Date().toISOString()

        notifications.value.forEach((n) => {
          if (!n.readAt) {
            n.readAt = now
          }
        })
      })
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }
  }

  const addNotification = (notification: Notification) => {
    notifications.value.unshift(notification)
  }

  return {
    notifications,
    loading,
    unreadNotifications,
    stats,
    allNotifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    addNotification,
  }
})
